import {
  IconAdjustments,
  IconBellCog,
  IconArchive,
  IconArrowsExchange,
  IconCalendarDollar,
  IconBuilding,
  IconCategory,
  IconChecklist,
  IconClockDollar,
  IconContract,
  IconDashboard,
  IconFileInvoice,
  IconFiles,
  IconFlag,
  IconWand,
  IconInbox,
  IconFileCheck,
  IconKey,
  IconHistory,
  IconReceipt,
  IconReceiptTax,
  IconReportAnalytics,
  IconRefreshAlert,
  IconLayoutGrid,
  IconShieldLock,
  IconShoppingCart,
  IconTags,
  IconTargetArrow,
  IconUsers,
  type IconProps,
  IconFileTypePdf,
  IconListCheck,
  IconUserCheck,
} from "@tabler/icons-react";
import type { ComponentType } from "react";
import { PERMISOS, PERMISOS_CATEGORIAS_DOCUMENTO, PERMISOS_TIPOS_DOCUMENTO, VER_SOLICITUDES } from "@/lib/permisos";

/**
 * Declaración de los items de navegación del AppShell. Cada uno declara los
 * PERMISOS que lo hacen visible (RBAC · Ruta B) · si no hay `permisos`, lo ven
 * todos. La filtración se hace en `filtrarNavParaPermisos` (helper puro · testeable).
 */
export interface NavItem {
  /**
   * Etiqueta del item · puede depender de los roles del usuario (ej. Mesa de
   * Control ve TODAS las solicitudes, no solo "las suyas" · decisión P29). La
   * etiqueta sigue siendo por ROL (es texto de display, no control de acceso).
   */
  label: string | ((roles: ReadonlyArray<string>) => string);
  href: string;
  icon: ComponentType<IconProps>;
  /** Permisos que hacen visible el item (basta tener CUALQUIERA). */
  permisos?: ReadonlyArray<string>;
  /**
   * Visible solo para los RESPONSABLES de algún tipo de solicitud de catálogo (por rol,
   * configurable · no hay permiso fijo). El navbar lo resuelve con `/solicitudes-catalogo/mis-tipos`.
   */
  soloResponsablesCatalogo?: boolean;
  /**
   * Visible solo para los REVISORES documentales (Gestión Documental · fase 3 · por los roles
   * revisores configurados, sin permiso fijo). El navbar lo resuelve con `/revision-documental/pendientes/count`.
   */
  soloRevisoresDocumentales?: boolean;
}

/** Resuelve la etiqueta final de un item para los roles del usuario actual. */
export function etiquetaNavItem(item: NavItem, roles: ReadonlyArray<string>): string {
  return typeof item.label === "function" ? item.label(roles) : item.label;
}

export const NAV_PRINCIPAL: ReadonlyArray<NavItem> = [
  { label: "Dashboard", href: "/dashboard", icon: IconDashboard },
  {
    // Mesa de Control ve TODAS las solicitudes del sistema, no solo "las suyas"
    // (decisión P29) · mismo espejo que el título de `MisSolicitudesPage`.
    label: (roles) =>
      roles.includes("MesaDeControl") ? "Solicitudes de pago" : "Mis solicitudes de pago",
    href: "/solicitudes",
    icon: IconFileInvoice,
    permisos: VER_SOLICITUDES,
  },
  {
    label: "Mis facturas",
    href: "/facturas",
    icon: IconReceipt,
    permisos: [PERMISOS.facturasVer],
  },
  {
    label: "Bandeja de autorización",
    href: "/autorizacion/bandeja",
    icon: IconChecklist,
    permisos: [PERMISOS.autorizacionAutorizar],
  },
  {
    // P44 · FB60 Contrapago · solicitudes Autorizadas en espera del CFDI
    // real (llega después del pago). Solicitante ve las suyas, Mesa de
    // Control ve todas (mismo criterio que el resto de bandejas P29).
    label: "Contrapagos pendientes",
    href: "/contrapagos-pendientes",
    icon: IconClockDollar,
    permisos: [PERMISOS.contrapagosGestionar],
  },
  {
    // Calendario de pagos (P39) · pagos programados · Mesa de Control (opera) y
    // Visor (solo lectura · sin reprogramar).
    label: "Calendario de pagos",
    href: "/calendario-pagos",
    icon: IconCalendarDollar,
    permisos: [PERMISOS.calendarioPagosVer],
  },
  {
    // Histórico Documental (P37) · acervo transversal · Mesa de Control (sus
    // sociedades) y Visor (solo lectura · todas las sociedades). El Solicitante
    // NO tiene acceso (RN-03 · ve sus documentos en el wizard).
    label: "Histórico documental",
    href: "/historico-documental",
    icon: IconArchive,
    permisos: [PERMISOS.historicoDocumentalVer],
  },
  {
    // Reportería (módulo de reporteo dinámico · S11). El acceso real lo decide
    // el backend por configuración; se ofrece a los roles que reportan y la
    // página muestra "sin acceso" a quien no tenga configuración asignada.
    label: "Reportería",
    href: "/reporteria",
    icon: IconReportAnalytics,
    permisos: [PERMISOS.reporteriaUsar],
  },
  {
    // Bandeja del módulo Solicitudes de catálogo · la ven los responsables de algún tipo.
    label: "Solicitudes de catálogo",
    href: "/solicitudes-catalogo",
    icon: IconInbox,
    soloResponsablesCatalogo: true,
  },
  {
    // Gestión Documental · fase 3 · documentos por revisar antes de la autorización.
    label: "Revisión documental",
    href: "/revision-documental",
    icon: IconFileCheck,
    soloRevisoresDocumentales: true,
  },
];

/**
 * Href del item con badge de solicitudes de catálogo pendientes ·
 * `AppShellNavbar` le monta el contador consultado por polling.
 */
export const HREF_SOLICITUDES_CATALOGO = "/solicitudes-catalogo";

/** Href del item con badge de documentos por revisar · `AppShellNavbar` le monta el contador. */
export const HREF_REVISION_DOCUMENTAL = "/revision-documental";

/** Un grupo (subcategoría) del menú de administración · agrupa items afines. */
export interface NavGrupo {
  titulo: string;
  items: ReadonlyArray<NavItem>;
}

/**
 * Menú de administración agrupado en subcategorías lógicas. El sidebar
 * renderiza cada grupo con su sub-encabezado y oculta los grupos vacíos (cuando
 * el usuario no ve ninguno de sus items · ej. un Auditor solo ve "Auditoría").
 * Orden dentro de cada grupo: por afinidad · mantenerlo al agregar items.
 */
export const NAV_ADMIN_GRUPOS: ReadonlyArray<NavGrupo> = [
  {
    titulo: "Organización y accesos",
    items: [
      { label: "Organizaciones", href: "/admin/organizaciones", icon: IconBuilding, permisos: [PERMISOS.adminOrganizaciones] },
      { label: "Áreas", href: "/admin/areas", icon: IconCategory, permisos: [PERMISOS.adminAreas] },
      { label: "Usuarios", href: "/admin/usuarios", icon: IconUsers, permisos: [PERMISOS.adminUsuarios] },
      { label: "Roles", href: "/admin/roles", icon: IconShieldLock, permisos: [PERMISOS.adminRoles] },
      { label: "Gestor de módulos", href: "/admin/modulos", icon: IconLayoutGrid, permisos: [PERMISOS.adminModulos] },
      { label: "Permisos", href: "/admin/permisos", icon: IconKey, permisos: [PERMISOS.adminPermisos] },
      { label: "Flujos de autorización", href: "/admin/flujos-autorizacion", icon: IconFlag, permisos: [PERMISOS.adminFlujosAutorizacion] },
      { label: "Prorrateo automático", href: "/admin/prorrateo-automatico", icon: IconWand, permisos: [PERMISOS.adminProrrateoAutomatico] },
    ],
  },
  {
    titulo: "Catálogos",
    items: [
      { label: "Proveedores", href: "/admin/proveedores", icon: IconBuilding, permisos: [PERMISOS.adminProveedores] },
      { label: "Catálogos contables", href: "/admin/catalogos-contables", icon: IconCategory, permisos: [PERMISOS.adminCatalogosContables] },
      { label: "Catálogo Prod/Serv SAT", href: "/admin/catalogos-sat", icon: IconCategory, permisos: [PERMISOS.adminCatalogosSat] },
      { label: "Cuentas por clave SAT", href: "/admin/mapeos-clave-cuenta", icon: IconTags, permisos: [PERMISOS.adminMapeosClaveCuenta] },
      { label: "Centros de costo por clave SAT", href: "/admin/clave-sat-centro-costo", icon: IconTargetArrow, permisos: [PERMISOS.adminClaveSatCentroCosto] },
      { label: "Indicadores de impuesto", href: "/admin/indicadores-impuesto", icon: IconReceiptTax, permisos: [PERMISOS.adminIndicadoresImpuesto] },
      { label: "Equivalencias de unidad", href: "/admin/equivalencias-unidad", icon: IconArrowsExchange, permisos: [PERMISOS.adminEquivalenciasUnidad] },
    ],
  },
  {
    // Gestión Documental (fase 2) · configuración del expediente.
    titulo: "Gestión documental",
    items: [
      { label: "Categorías de documento", href: "/admin/categorias-documento", icon: IconFileTypePdf, permisos: PERMISOS_CATEGORIAS_DOCUMENTO },
      { label: "Tipos de documento", href: "/admin/tipos-documento", icon: IconFiles, permisos: PERMISOS_TIPOS_DOCUMENTO },
      { label: "Requisitos documentales", href: "/admin/requisitos-documentales", icon: IconListCheck, permisos: [PERMISOS.adminGestionDocumental] },
      { label: "Reglas de revisión", href: "/admin/reglas-revision-documental", icon: IconUserCheck, permisos: [PERMISOS.adminGestionDocumental] },
    ],
  },
  {
    titulo: "Documentos de compra (SAP)",
    items: [
      { label: "Órdenes de compra", href: "/admin/ordenes-compra", icon: IconShoppingCart, permisos: [PERMISOS.adminComprasSap] },
      { label: "Contratos anualizados", href: "/admin/contratos-anualizados", icon: IconContract, permisos: [PERMISOS.adminComprasSap] },
    ],
  },
  {
    titulo: "Sistema",
    items: [
      // Configuración del módulo Solicitudes de catálogo (responsables, textos, atención automática).
      { label: "Tipos de solicitud de catálogo", href: "/admin/tipos-solicitud-catalogo", icon: IconInbox, permisos: [PERMISOS.adminTiposSolicitudCatalogo] },
      { label: "Reportería", href: "/admin/reporteria", icon: IconReportAnalytics, permisos: [PERMISOS.reporteriaAdministrar] },
      { label: "Notificaciones", href: "/admin/notificaciones", icon: IconBellCog, permisos: [PERMISOS.adminNotificaciones] },
      { label: "Configuraciones", href: "/admin/configuraciones", icon: IconAdjustments, permisos: [PERMISOS.adminConfiguraciones] },
      // Hotspot 55 · eventos del outbox que fallaron al procesarse (reproceso manual).
      { label: "Eventos fallidos", href: "/admin/eventos-fallidos", icon: IconRefreshAlert, permisos: [PERMISOS.adminEventosFallidos] },
      { label: "Auditoría", href: "/admin/auditoria", icon: IconHistory, permisos: [PERMISOS.auditoriaVer] },
    ],
  },
];

/** Lista plana de TODOS los items de administración (deriva de los grupos). */
export const NAV_ADMIN: ReadonlyArray<NavItem> = NAV_ADMIN_GRUPOS.flatMap((g) => g.items);

/**
 * Filtra una lista de items a los que el usuario actual puede ver según sus
 * PERMISOS. Items sin `permisos` son visibles para todos.
 */
export function filtrarNavParaPermisos(
  items: ReadonlyArray<NavItem>,
  permisos: ReadonlyArray<string>,
  esResponsableCatalogo = false,
  esRevisorDocumental = false
): NavItem[] {
  return items.filter((i) =>
    i.soloResponsablesCatalogo
      ? esResponsableCatalogo
      : i.soloRevisoresDocumentales
        ? esRevisorDocumental
        : !i.permisos || i.permisos.some((p) => permisos.includes(p))
  );
}
